import {
  Circle,
  Close,
  DoneAll,
  NotificationsActive,
  TagFaces,
} from "@mui/icons-material";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Fragment } from "react/jsx-runtime";
import moment from "moment";
import Config from "../../Utils/Config";
import styles from "../../Styles/mainHeader.module.css";
import { useContext, useEffect, useState } from "react";
import { endpoints } from "../../Utils/UrlEnum";
import { WebSocketContext } from "../../Context/WebSocketContext";
import { updateData } from "../../Services/updateData";

export default function Notifications() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const websocket = useContext(WebSocketContext);

  /**
   * Request browser notification permission on component mount
   */
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  /**
   * Calculate unread notification count
   */
  const calculateUnreadCount = (notificationsList: any[]) => {
    if (!notificationsList || !Array.isArray(notificationsList)) {
      return 0;
    }
    return notificationsList.filter((n) => !n.seen).length;
  };

  /**
   * Load initial notifications and set up socket listeners
   */
  useEffect(() => {
    const handleProductsUpdate = (data: any) => {
      setNotifications(data.notifications);
      // Only count unread notifications
      const unreadCount = calculateUnreadCount(data.notifications);
      setNotificationCount(unreadCount);
    };

    const handleNewNotification = (notification: any) => {
      // Add new notification to the beginning of the list
      setNotifications((prevNotifications) => {
        const updatedNotifications = [notification, ...prevNotifications];
        // Update count based on unread notifications only
        const unreadCount = calculateUnreadCount(updatedNotifications);
        setNotificationCount(unreadCount);
        return updatedNotifications;
      });

      // Show customized browser notification if permission is granted
      if ("Notification" in window && Notification.permission === "granted") {
        const browserNotification = new Notification(
          Vocabulary.notification,
          {
            body: notification.message,
            icon: "/logo.png",
            badge: "/logo.png",
            tag: `notification-${notification._id || Date.now()}`,
            requireInteraction: false,
            silent: false,
          }
        );

        // Handle notification click - focus the app window
        browserNotification.addEventListener("click", () => {
          window.focus();
          browserNotification.close();
        });
      }
    };

    websocket.socket.emit("getNotifications", {
      userId: localStorage.getItem("userId"),
    });
    websocket.socket.on("notifications", handleProductsUpdate);
    websocket.socket.on("notification:new", handleNewNotification);

    return () => {
      websocket.socket.off("notifications", handleProductsUpdate);
      websocket.socket.off("notification:new", handleNewNotification);
    };
  }, [websocket]);
  /**
   *
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Mark a single notification as seen
   */
  const handleNotificationClick = (notificationId: string, isSeen: boolean) => {
    if (!isSeen) {
      updateData(`${endpoints.notifications.markAsRead}`, {
        notificationsIds: [notificationId],
      }).then((res: any) => {
        if (res) {
          // Update the notification in the list locally
          setNotifications((prevNotifications) =>
            prevNotifications.map((notification) =>
              notification._id === notificationId
                ? { ...notification, seen: true }
                : notification
            )
          );
          // Update the unread count
          setNotifications((prevNotifications) => {
            const unreadCount = calculateUnreadCount(prevNotifications);
            setNotificationCount(unreadCount);
            return prevNotifications;
          });
        }
      });
    }
  };

  /**
   *
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Fab
        id="notificationsFab"
        size="small"
        className={`${styles.fab}`}
        onClick={handleClick}
      >
        <Badge
          badgeContent={notificationCount}
          max={Config.maxNotificationCount}
          classes={{ badge: styles.badge }}
        >
          <NotificationsActive />
        </Badge>
      </Fab>
      <Popover
        id={id}
        open={open}
        className="notificationsPopover"
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Card sx={{ width: 450, maxHeight: 600, overflow: "auto" }}>
          <CardHeader
            subheader={Vocabulary.notifications}
            action={
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            }
            sx={{ padding: "16px" }}
          />
          <CardContent sx={{ padding: 0 }}>
            <List sx={{ padding: 0 }}>
              {notifications && notifications.length > 0 ? (
                notifications?.map((notification: any) => {
                  return (
                    <Fragment key={notification._id}>
                      <ListItem
                        className={`${
                          notification.seen
                            ? styles.notificationRead
                            : styles.notificationNotRead
                        }`}
                        onClick={() =>
                          handleNotificationClick(notification._id, notification.seen)
                        }
                        sx={{
                          cursor: "pointer",
                          padding: "16px",
                          transition: "background-color 0.2s ease",
                          "&:hover": {
                            backgroundColor: notification.seen
                              ? "rgba(0, 0, 0, 0.02)"
                              : "rgba(25, 118, 210, 0.04)",
                          },
                        }}
                      >
                        <ListItemAvatar
                          sx={{
                            minWidth: 40,
                            color: notification.seen ? "text.secondary" : "primary.main",
                          }}
                        >
                          {notification.seen ? <DoneAll /> : <Circle />}
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <span
                              style={{
                                fontWeight: notification.seen ? 400 : 600,
                                color: notification.seen
                                  ? "rgba(0, 0, 0, 0.6)"
                                  : "rgba(0, 0, 0, 0.87)",
                              }}
                            >
                              {notification.message}
                            </span>
                          }
                          secondary={moment(notification.createdAt).format(
                            Config.momentEUDateTimeFormat
                          )}
                          sx={{
                            marginLeft: 1,
                            "& .MuiListItemText-primary": {
                              lineHeight: 1.5,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            },
                          }}
                        />
                      </ListItem>
                      <Divider sx={{ margin: 0 }} />
                    </Fragment>
                  );
                })
              ) : (
                <ListItem
                  sx={{
                    padding: "32px 16px",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <ListItemAvatar sx={{ marginBottom: 2 }}>
                    <TagFaces sx={{ fontSize: 48, color: "text.secondary" }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={Vocabulary.noNotifications}
                    sx={{ color: "text.secondary" }}
                  />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
}
